import { brandFromAnswers } from '@starter/funnel';
import { Button } from '@starter/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { setBrandLogo, useCreateBrand, useUpdateBrand } from '../brands/api.ts';
import { logoDraftAsFile } from '../funnel/logo-draft.ts';
import { clearSaved, readSaved } from '../funnel/state.ts';
import { authClient } from '../lib/auth.ts';

/**
 * `/welcome` — the one screen between the funnel and the product.
 *
 * The funnel asks thirty-eight screens' worth of questions in a browser that
 * has no account yet, so the answers sit in `localStorage` until there is
 * somewhere to put them (`funnel/state.ts`). This route is that somewhere: it
 * is the first address a new account lands on, it turns the answers into a
 * brand, and it forgets them.
 *
 * It exists as a screen rather than as an effect on the home page because it
 * makes two requests that can fail, and a failure needs somewhere to be shown.
 * Nobody stays here: with answers it ends on the new brand, without them on
 * the home page, and a reload after either does the second thing.
 */
function Welcome() {
  const navigate = Route.useNavigate();
  /*
   * Read once, before anything is created. The state is cleared on success, so
   * reading it again on a later render would find nothing and undo the screen
   * mid-flight.
   */
  const [draft] = useState(() => {
    const saved = readSaved();
    return saved ? brandFromAnswers(saved.answers) : undefined;
  });
  const create = useCreateBrand();
  const update = useUpdateBrand();
  /** Requests are not a render. React may run this effect twice; the brand is made once. */
  const started = useRef(false);
  /** Set the moment the brand exists, so a retry finishes it instead of making a second. */
  const made = useRef<string>(undefined);
  const [failed, setFailed] = useState<string>();

  const run = useCallback(async () => {
    if (!draft) {
      void navigate({ to: '/', replace: true });
      return;
    }

    try {
      /*
       * The name is the create call; everything else is one patch, which the
       * API applies all or none. The two together are not a transaction, and
       * deliberately so: if the second fails the brand still exists under the
       * name they typed, and the place we send them to is the page where every
       * one of these fields is editable anyway. The alternative — no brand at
       * all because the tone did not take — is worse.
       */
      made.current ??= (await create.mutateAsync(draft.displayName)).brand.id;
      const brandId = made.current;

      const fields = {
        ...(draft.website ? { website: draft.website } : {}),
        ...(draft.vertical ? { vertical: draft.vertical } : {}),
        ...(draft.tone ? { tone: draft.tone } : {}),
      };
      if (Object.keys(fields).length > 0) await update.mutateAsync({ id: brandId, fields });

      /*
       * The photograph, if one was chosen — the first moment it can go
       * anywhere, because until now there was no account to own a file.
       *
       * **It fails loudly**, into the same retry the rest of this screen uses.
       * The first version swallowed the error on the reasoning that a brand
       * without its picture is still a brand — and that is the reasoning that
       * produced this bug in the first place: the funnel asked for a
       * photograph and quietly dropped it. Losing it a second time, silently,
       * would be the same mistake with a better excuse.
       *
       * Retrying is safe and cheap: `made.current` holds the brand, so a
       * second attempt uploads again rather than making a second brand.
       */
      const logo = logoDraftAsFile();
      if (logo) await setBrandLogo(brandId, logo);

      // Only once it is somewhere durable. A cleared funnel and a failed
      // request is the one combination that loses the two minutes.
      clearSaved();
      await navigate({ to: '/brands/$brandId', params: { brandId }, replace: true });
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Something went wrong');
    }
  }, [create, draft, navigate, update]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void run();
  }, [run]);

  if (failed) {
    return (
      <Middle testId="welcome-failed">
        <h1 className="font-display text-[24px] leading-[30px] font-bold text-ink">
          Your answers are still here
        </h1>
        <p className="mt-2 text-[15px] leading-[23px] text-ink-muted">
          The brand could not be set up: {failed}. Nothing was lost — everything you told the funnel
          is still saved in this browser.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Button
            data-testid="welcome-retry"
            onClick={() => {
              setFailed(undefined);
              void run();
            }}
          >
            Try again
          </Button>
          <Button onClick={() => void navigate({ to: '/' })} variant="secondary">
            Skip for now
          </Button>
        </div>
      </Middle>
    );
  }

  return (
    <Middle testId="welcome-working">
      <h1 className="font-display text-[24px] leading-[30px] font-bold text-ink">
        Setting up {draft?.displayName ?? 'your account'}
      </h1>
      <p className="mt-2 text-[15px] leading-[23px] text-ink-muted">
        Putting everything you told us into the brand, so you do not type it twice.
      </p>
      {/*
        The same bar the funnel's own waiting screen uses, and for the same
        reason: this is the screen straight after it, and something that does
        not move reads as something that has stopped.
      */}
      <div className="mx-auto mt-6 h-1.5 w-40 overflow-hidden rounded-full bg-surface-muted">
        <span className="block h-full w-2/3 animate-pulse rounded-full bg-ink" />
      </div>
    </Middle>
  );
}

/**
 * The column, for a screen nobody stays on.
 *
 * Centred rather than started at the top, which every other screen in the app
 * is: those are places, this is a moment between two of them, and a paragraph
 * pinned to the top of an empty page reads as a page that failed to load the
 * rest of itself.
 */
function Middle({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="max-w-md text-center" data-testid={testId}>
        {children}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/welcome')({
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) throw redirect({ to: '/login', search: { redirectTo: location.href } });
  },
  component: Welcome,
});
